import {
  MixpanelEvent,
  MixpanelEventProperty,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  tabLabelerEnumerated,
  useCallbackRef,
  useMixpanel,
  usePersistedTabs,
  useTabs,
  type BlotterColumnState,
  type TabProps,
  type UseTabsProps,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';
import { useBlotterTableTabCloning } from '../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import {
  DEFAULT_TRANSFERS_TABS,
  NEW_TRANSFER_TAB_DEFAULTS,
  RECENT_TRANSFERS_TAB_ID,
  TRANSFERS_TABS_EXCLUDED_IDS,
} from './defaults';
import { FilteredTransfersWrapper } from './FilteredTransfersWrapper';
import { TRANSFERS_BLOTTER_PREFIX } from './tokens';
import { TransfersFilterRow } from './TransfersFilterRow';
import type { TransfersBlotterFilter } from './useTransfersFilter';

export interface TransfersTabProps extends TabProps {
  defaultFilter: Partial<TransfersBlotterFilter>;
  defaultColumns?: BlotterColumnState[];
}

const tabLabeler = tabLabelerEnumerated('Transfers');
function getBlotterID(tabID: string | undefined) {
  return `${TRANSFERS_BLOTTER_PREFIX}/${tabID}`;
}

export function TransfersBlotter(props?: { showNewTransfer?: boolean }) {
  const { showNewTransfer = true } = props ?? {};
  const mixpanel = useMixpanel();

  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);

  const handleSelect = useCallback(() => setInitialFiltersOpen(false), []);
  const handleAdd = useCallback(() => {
    mixpanel.track(MixpanelEvent.AddTab);
    setInitialFiltersOpen(true);
  }, [mixpanel]);

  const persistedTabs = usePersistedTabs<TransfersTabProps>(TRANSFERS_BLOTTER_PREFIX, {
    defaultInitialItems: DEFAULT_TRANSFERS_TABS,
    defaultInitialSelectedIndex: 0,
    excludedIDs: TRANSFERS_TABS_EXCLUDED_IDS,
    onSelect: handleSelect,
  });

  // `useTabs` re-fires when the onSelect callback identity changes, so this wrapper must stay stable
  // while still seeing the latest mixpanel instance and persisted tab handlers.
  const onTabSelected = useCallbackRef<NonNullable<UseTabsProps<TransfersTabProps>['onSelect']>>((index, tabs) => {
    mixpanel.track(MixpanelEvent.SetTransfersBlotterTab, {
      [MixpanelEventProperty.TabIndex]: index,
      [MixpanelEventProperty.TabLabel]: tabs?.[index]?.label ?? '',
    });
    persistedTabs.onSelect(index, tabs);
  }) satisfies UseTabsProps['onSelect'];

  const tabs = useTabs<TransfersTabProps>({
    ...persistedTabs,
    initialItems: persistedTabs.initialItems,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
    onSelect: onTabSelected,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });

  const handleCloneTab = useCallback(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    tabs.clone(tabs.selectedIndex, {
      closable: true,
      reorderable: true,
    });
  }, [mixpanel, tabs]);

  return (
    <>
      <Tabs {...tabs} h="100%" size={TabSize.Large}>
        <TabList isBordered rightItems={<TransfersFilterRow showNewTransfer={showNewTransfer} />}>
          {tabs.items.map((tab, idx) => (
            <Tab key={idx} {...tab} reorderable={tab.id !== RECENT_TRANSFERS_TAB_ID} />
          ))}
        </TabList>
        <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
          {tabs.items.map(tab => (
            <FilteredTransfersWrapper
              key={tab.id}
              tabId={tab.id!}
              blotterID={`${TRANSFERS_BLOTTER_PREFIX}/${tab.id}`}
              initialIsOpen={initialFiltersOpen}
              defaultColumns={tab.defaultColumns}
              defaultFilter={tab.defaultFilter ?? NEW_TRANSFER_TAB_DEFAULTS.defaultFilter}
              onCloneTab={handleCloneTab}
            />
          ))}
        </TabPanels>
      </Tabs>
    </>
  );
}
